import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ListOptions } from '../models/list-options';
import { Organization } from '../models/organization';
import { Property } from '../models/property';
import { ServiceBase } from './service-base';

@Injectable({
    providedIn: 'root',
})
export class OrganizationsService extends ServiceBase {
    constructor(private http: HttpClient) {
        super();
        this.currentOrganizationSubject = new BehaviorSubject<Organization>(JSON.parse(sessionStorage.getItem('currentOrganization')));
    }

    /**
     * Sujet pour indiquer l'organisation en cours
     */
    private currentOrganizationSubject: BehaviorSubject<Organization>;

    /**
     * Propriété pour indiquer le changement de l'organization en cours
     */
    public get currentOrganizationChange$() {
        return this.currentOrganizationSubject.asObservable();
    }

    getOrganisation(organizationId: string, globalNotification = true, executeOption = false): Observable<Organization> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('global-notification', String(globalNotification))
                .set('X-OrganizationId', !executeOption ? '' : this.currentOrganizationSubject?.value ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.get<Organization>(`${this.baseUrl}/api/organizations/${organizationId}`, httpOptions);
    }

    getOrganizationWithDisplayParentOrganization(organizationId: string): Observable<Organization> {
        return this.getOrganisation(organizationId).pipe(
            switchMap((organization: Organization) => {
                if (this.currentOrganizationSubject.getValue().id === organization.id) {
                    organization.displayParentOrganization = '';
                    return of(organization);
                } else {
                    return this.getOrganisation(organization.organizationId, false).pipe(
                        map((parentOrganization) => {
                            // Affecte le nom de l'organisation parente
                            organization.displayParentOrganization = parentOrganization.display;

                            return organization;
                        }),
                        catchError(() => {
                            organization.displayParentOrganization = '';

                            // En cas d'erreur, organisation parente non trouvée, retourne l'organisation sans le nom de l'organisation parente
                            return of(organization);
                        })
                    );
                }
            })
        );
    }

    getOrganisations(executeOption: boolean = true, filter: string = null, listOptions: ListOptions = null): Observable<Organization[]> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', !executeOption ? '' : this.currentOrganizationSubject?.value ? this.currentOrganizationSubject.value.id : ''),
        };

        if (listOptions) {
            httpOptions.headers = listOptions.addListOptions(httpOptions.headers);
        }

        const urlString = filter ? `${this.baseUrl}/api/organizations?filter=${encodeURIComponent(filter)}` : `${this.baseUrl}/api/organizations`;
        const organizations = this.http.get<Organization[]>(urlString, httpOptions);

        // Récupère le nom de l'organisation parente
        return organizations.pipe(
            map((orgs) => {
                orgs.forEach((org) => {
                    const displayParentOrg = orgs.find((x) => x.id === org.organizationId);
                    org.displayParentOrganization = !!displayParentOrg ? displayParentOrg.display : '';
                });

                return orgs;
            })
        );
    }

    addOrganization(organization: Organization): Observable<Organization> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('X-OrganizationId', this.currentOrganizationSubject?.value ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.post<Organization>(`${this.baseUrl}/api/organizations`, organization, httpOptions);
    }

    deleteOrganization(organizationId: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.currentOrganizationSubject?.value ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.delete(`${this.baseUrl}/api/organizations/${organizationId}`, httpOptions);
    }

    deleteOrganizationProperty(organizationId: string, propertyName: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.currentOrganizationSubject ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.delete(`${this.baseUrl}/api/organizations/${organizationId}/properties/${propertyName}`, httpOptions);
    }

    deleteOrganizationRight(organizationId: string, rightName: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.currentOrganizationSubject ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.delete(`${this.baseUrl}/api/organizations/${organizationId}/rights/${rightName}`, httpOptions);
    }

    updateOrganization(organization: Organization): Observable<Organization> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('X-OrganizationId', this.currentOrganizationSubject?.value ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.patch<Organization>(`${this.baseUrl}/api/organizations/${organization.id}`, organization, httpOptions);
    }

    updateOrganizationProperty(organizationId: string, propertyName: string, propertyValue: string): Observable<Property> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('X-OrganizationId', this.currentOrganizationSubject ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.put<Property>(`${this.baseUrl}/api/organizations/${organizationId}/properties/${propertyName}`, JSON.stringify(propertyValue), httpOptions);
    }

    updateOrganizationRight(organizationId: string, rightName: string, rightValue: number): Observable<Property> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('X-OrganizationId', this.currentOrganizationSubject ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.put<Property>(`${this.baseUrl}/api/organizations/${organizationId}/rights/${rightName}`, rightValue, httpOptions);
    }

    getCurrentOrganization(): Organization {
        return this.currentOrganizationSubject.value;
    }

    setCurrentOrganization(organization: Organization) {
        if (organization == null) {
            this.removeCurrentOrganization();
        } else {
            sessionStorage.setItem('currentOrganization', JSON.stringify(organization));
            console.log('Current Organization : ' + organization.id);
        }
        this.currentOrganizationSubject.next(organization);
    }

    removeCurrentOrganization() {
        sessionStorage.removeItem('currentOrganization');
        console.log('Current Organization : <null>');
        this.currentOrganizationSubject.next(null);
    }
}
